<template>
	<div class="hotel-name__social">
		<a
			ref="favoriteButton"
			class="hotel-name__social-link"
			href="#"
			@click.prevent="handleFavorite()"
		>
			<title class="hotel-name__social-title">{{ favoriteIconTitle }}</title>
			<i
				ref="favoriteButtonIcon"
				class="fa-solid fa-heart hotel-name__social-icon"
				:class="{ 'hotel-name__social-icon--active': isFavorite }"
				aria-hidden="true"
			>
			</i>
			{{ favoriteIconText }}
		</a>

		<a
			data-ga-event="share"
			data-ga-content_type="product"
			:data-ga-item_id="hotelDetails.Hotel.IffCode"
			class="hotel-name__social-link"
			href="#"
			@click.prevent="handleSharing()"
		>
			<i
				:class="shareIconClass"
				aria-hidden="true"
			></i>
			teilen
		</a>
	</div>
</template>

<script lang="ts" setup>
import { getWebsiteName } from '@utils/environmentUtils';
import { ref, computed, onBeforeMount, onUnmounted } from 'vue';
import type { HotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import { WEB_CLIENTS_NAMES } from '@global-js/constants';
import { addEntryToFavoritePersistence, removeEntryFromFavoritePersistence } from '@/js/modules/bookmark';
import BookmarkAnalytics from '@/js/modules/analytics/BookmarkAnalytics';
import type { MergedOfferItemData } from '@/interfaces/offer';
import ShareLinkAnalytics from '@/js/modules/analytics/ShareLinkAnalytics';
import { EventBus } from '@global-js/event-bus';
import type { FavoriteHotel } from '@/interfaces/api/v1-hotel-list';
import { isFavorite as isInFavorites } from '@/js/modules/bookmark';
import { getOS } from '@utils/environmentUtils';

interface Props {
	hotel: HotelData;
	bestOffer: Partial<MergedOfferItemData>;
}
const props = withDefaults(defineProps<Props>(), {});

const hotelDetails = computed(() => ({
	Hotel: props.hotel.Hotel,
	Location: props.hotel.Location,
	RatingCount: props.hotel.RatingCount,
	RatingOverallValue: props.hotel.RatingOverallValue,
}));

const isFavorite = ref(false);
const clientName = getWebsiteName();
const favoriteIconTitle = computed((): string => (isFavorite.value ? 'Hotel von Merkzettel entfernen' : 'Hotel zu Merkzettel hinzufügen'));
const favoriteIconText = computed((): string => (isFavorite.value ? 'Gemerkt' : 'Merken'));

const rating = computed((): number => {
	const { Hotel } = hotelDetails.value;
	return Number(Hotel?.Category);
});

const handleFavorite = (): void => {
	const item = BookmarkAnalytics.fromHotelFeatures(hotelDetails.value.Hotel, hotelDetails.value.Location, props.bestOffer);
	if (!isFavorite.value) {
		addEntryToFavoritePersistence(item, rating.value);
	} else {
		removeEntryFromFavoritePersistence(item);
	}
};

const handleSharing = (): void => {
	const pageTitle = document.title ? document.title : WEB_CLIENTS_NAMES.at;
	const subject = `Schau dir dieses ${clientName}-Angebot an!`;
	const queryString = window.location.search ? `${window.location.search}&` : '?';
	const url = `${window.location.origin}${window.location.pathname}${queryString}utm_source=share`;
	const shareAnalytics = ShareLinkAnalytics.createInstance(String(props.hotel.Hotel.IffCode));
	shareAnalytics.fireShareLinkDomReady();

	if (navigator.share) {
		navigator
			.share({
				title: pageTitle,
				text: subject,
				url,
			})
			.catch(function (error) {
				console.log('Error sharing', error);
			});
	} else {
		window.location.href =
			'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(pageTitle + ' \n' + url + '\n\n');
	}
};

const updateBookmark = (iffCode: number, added: boolean): void => {
	if (iffCode === hotelDetails.value.Hotel.IffCode) {
		isFavorite.value = added;
	}
};

const checkForFavorites = async (): Promise<void> => {
	isFavorite.value = await isInFavorites(hotelDetails.value.Hotel.IffCode);
};

const osInfo = getOS();

const shareIconClass = computed(() => {
	if (osInfo.isAndroid) {
		return 'fa-solid fa-share-nodes hotel-name__share-icon';
	} else if (osInfo.isiOS) {
		return 'fa-kit fa-share-ios hotel-name__share-icon';
	} else {
		return 'fa-solid fa-share-from-square hotel-name__share-icon';
	}
});

onBeforeMount((): void => {
	EventBus.$on('bookmark:favoriteChange', updateBookmark);
	EventBus.$on('bookmark:hotelsUpdated', (hotels: FavoriteHotel[]) => {
		isFavorite.value = hotels.some((h) => h.IffCode === hotelDetails.value.Hotel.IffCode);
	});

	checkForFavorites();
});

onUnmounted((): void => {
	EventBus.$off('bookmark:favoriteChange', updateBookmark);
	EventBus.$off('bookmark:hotelsUpdated');
});
</script>

<style lang="scss" scoped>
$top-margin: 1rem;

.hotel-name {
	&__social {
		display: flex;
		float: right;
		margin-top: $top-margin;
		height: fit-content;
		margin-left: auto;

		.hotel-info__container & {
			margin-top: 0;
		}
	}

	&__social-link {
		width: 5rem;
		color: $color-text-l5;
		text-decoration: none;
		text-align: center;
		font-size: 0.9rem;
		text-transform: uppercase;
		display: flex;
		flex-direction: column;

		&:hover {
			color: $color-link;

			i:not(.hotel-name__social-icon--active) {
				color: $color-link;
			}
		}

		i {
			font-size: 2rem;
		}
	}

	&__social-icon {
		display: block;
		width: 2.3rem;
		height: 2.3rem;
		margin: 0 auto 0.25rem;
		color: $color-text-l5;
		font-size: 2rem;

		&--active {
			color: $color-extra;
			transition: color 0.5s;
			animation: rpb_heartbeat 0.25s 2;
		}

		&--disabled {
			color: $color-text-l5;
		}
	}

	&__title-container {
		display: flex;
		flex-wrap: wrap;
	}

	&__title {
		padding: 0;
		font-family: $font-family-special;
		font-size: 2.6rem;
		font-weight: $font-weight-bold;
		text-align: left;
		margin-top: $top-margin;
		margin-right: 1rem;
	}

	&__rating {
		vertical-align: top;
		white-space: nowrap;
		margin-top: $top-margin;
	}

	&__share-icon {
		margin-bottom: 0.5rem;
	}
}
</style>
